import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Free drag for a `position: fixed` element, desktop only.
 *
 * Moves the element with inline `left`/`top`, never `transform`: a transform
 * would make the element the containing block for any fixed descendant (the
 * chat panel lives inside the avatar widget) and pin it to the widget.
 *
 * - Drag starts on `handleRef` only, never from a button/link inside it.
 * - Pointer capture is deferred until the pointer has moved `threshold` px,
 *   so an ordinary click inside the handle behaves exactly as before.
 * - The click that follows a real drag is swallowed in the capture phase.
 * - The dropped position is saved to localStorage once on drop and kept as
 *   the user's intent; what's displayed is that position clamped to the
 *   current viewport, so shrinking the window pulls it into view and
 *   widening it again puts it back where it was dropped.
 * - Below `minWidth` the hook stands down and the element's own CSS corner
 *   placement applies; the saved position comes back when it widens again.
 */

const NO_DRAG = "button, a, input, textarea, select, [data-no-drag]";

function readStored(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (v && v.v === 1 && Number.isFinite(v.x) && Number.isFinite(v.y)) {
      return { x: v.x, y: v.y };
    }
  } catch {
    /* unavailable or corrupt — fall back to the default corner */
  }
  return null;
}

function writeStored(key, pos) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ v: 1, x: pos.x, y: pos.y }));
  } catch {
    /* storage full or blocked — the position just won't persist */
  }
}

// Height of whatever is pinned across the top of the page (read from a CSS
// custom property, so it follows the stylesheet), plus the margin.
function topLimit(topVar, margin) {
  if (!topVar) return margin;
  const v = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(topVar)
  );
  return (Number.isFinite(v) ? v : 0) + margin;
}

function clampToViewport(x, y, el, margin, topVar) {
  // clientWidth/Height exclude the scrollbar gutter; innerWidth doesn't.
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;
  const minY = topLimit(topVar, margin);
  const maxX = Math.max(margin, vw - el.offsetWidth - margin);
  const maxY = Math.max(minY, vh - el.offsetHeight - margin);
  return {
    x: Math.round(Math.min(Math.max(x, margin), maxX)),
    y: Math.round(Math.min(Math.max(y, minY), maxY)),
  };
}

export default function useDraggablePosition({
  storageKey,
  minWidth = 640,
  margin = 16,
  threshold = 6,
  // CSS custom property holding the height of a fixed top bar; the element
  // is kept below it so it can never be dropped where it can't be grabbed.
  topVar = null,
}) {
  const containerRef = useRef(null);
  const handleRef = useRef(null);
  const query = `(min-width: ${minWidth}px)`;

  const [enabled, setEnabled] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches
  );
  // `pos` is where the user dropped it; `shown` is `pos` clamped to the
  // current viewport and is what's rendered.
  const [pos, setPos] = useState(() => readStored(storageKey));
  const [shown, setShown] = useState(pos);
  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setEnabled(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  // Keep the saved spot on screen: on mount, when drag re-enables, on window
  // resize and whenever the widget itself grows (control bar, voice controls).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!enabled || !pos || !el) return undefined;

    const apply = () => {
      if (dragRef.current?.moved) return;
      const next = clampToViewport(pos.x, pos.y, el, margin, topVar);
      setShown((s) => (s && s.x === next.x && s.y === next.y ? s : next));
    };
    let frame = 0;
    const reclamp = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("resize", reclamp);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(reclamp) : null;
    ro?.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", reclamp);
      ro?.disconnect();
    };
  }, [enabled, pos, margin, topVar]);

  const onMove = useCallback(
    (e) => {
      const d = dragRef.current;
      const el = containerRef.current;
      if (!d || !el || e.pointerId !== d.id) return;
      const dx = e.clientX - d.px;
      const dy = e.clientY - d.py;

      if (!d.moved) {
        if (Math.hypot(dx, dy) < threshold) return;
        d.moved = true;
        try {
          handleRef.current?.setPointerCapture(d.id);
        } catch {
          /* pointer already gone */
        }
        document.body.style.userSelect = "none";
        setIsDragging(true);
      }

      e.preventDefault();
      d.last = clampToViewport(d.x0 + dx, d.y0 + dy, el, margin, topVar);
      // Straight to the DOM: no React render of the avatar tree per move.
      el.style.left = `${d.last.x}px`;
      el.style.top = `${d.last.y}px`;
      el.style.right = "auto";
      el.style.bottom = "auto";
    },
    [margin, threshold, topVar]
  );

  const endDrag = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.id) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      try {
        handleRef.current?.releasePointerCapture(d.id);
      } catch {
        /* not captured */
      }
      dragRef.current = null;
      if (!d.moved) return;

      document.body.style.userSelect = "";
      setIsDragging(false);
      if (e.type === "pointercancel" || !d.last) {
        // Put it back exactly where React left it.
        const el = containerRef.current;
        if (el) Object.assign(el.style, d.startStyle);
        return;
      }
      suppressClickRef.current = true;
      setPos(d.last);
      setShown(d.last);
      writeStored(storageKey, d.last);
    },
    [onMove, storageKey]
  );

  const onPointerDown = useCallback(
    (e) => {
      if (!enabled || e.button !== 0 || e.pointerType === "touch") return;
      if (e.target.closest(NO_DRAG)) return;
      const el = containerRef.current;
      if (!el) return;
      suppressClickRef.current = false;
      const r = el.getBoundingClientRect();
      dragRef.current = {
        id: e.pointerId,
        px: e.clientX,
        py: e.clientY,
        x0: r.left,
        y0: r.top,
        moved: false,
        last: null,
        startStyle: {
          left: el.style.left,
          top: el.style.top,
          right: el.style.right,
          bottom: el.style.bottom,
        },
      };
      // Stops text selection and the browser's native image/video drag.
      e.preventDefault();
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", endDrag);
      window.addEventListener("pointercancel", endDrag);
    },
    [enabled, onMove, endDrag]
  );

  const onClickCapture = useCallback((e) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    e.stopPropagation();
    e.preventDefault();
  }, []);

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      if (dragRef.current?.moved) document.body.style.userSelect = "";
    },
    [onMove, endDrag]
  );

  const style =
    enabled && pos && shown
      ? { left: shown.x, top: shown.y, right: "auto", bottom: "auto" }
      : undefined;

  return {
    containerRef,
    handleRef,
    style,
    isDragging,
    enabled,
    handleProps: { onPointerDown, onClickCapture },
  };
}
